import * as PIXI from "pixi.js";
import gsap from "gsap";
import { Symbol } from "../Symbol";
import { IReel, ReelState } from "src/Panel/Reel";
import { GameEngine } from "../server/GameEngine";
import { Spine } from "@pixi/spine-pixi";
import { soundManager } from "src/shared/UI/SoundManager";
import { SoundInstance } from "src/shared/UI/SoundManagerUtils";

const SPIN_SPEED_NORMAL: number = 0.05; // Normal spin speed
const SPIN_SPEED_ANTICIPATION: number = 0.2; // Normal spin speed
const STOP_SPEED_NORMAL: number = 0.4; // Normal spin speed
const STOP_SPEED_ANTICIPATION: number = 1.5; // Normal spin speed

export class ReelSpin extends PIXI.Container implements IReel {
  private reelData: string[] = []; // Array holding the reel's symbol data
  private symbols: Symbol[] = []; // Array holding the symbols on the reel
  private anticipationSkeleton!: Spine; // Delay before starting the spin
  private currentState: ReelState = ReelState.IDLE; // Current state of the reel
  private delay: number = 0; // Delay before starting the spin
  private spinSpeed: number = 0; // Spin speed for the reel
  private stopSpeed: number = 0; // Spin speed for the reel
  private isStop: boolean = false; // Flag to stop the reel
  private tween!: gsap.core.Tween; // GSAP tween instance for animations
  private quickStop: boolean = false; // Flag to indicate if the reel should stop quickly
  private index: number = 0; // Index of the reel
  private rowsToHide: number[] = []; // Stores the row indexes to hide when stopping
  private content: PIXI.Container<PIXI.ContainerChild>;

  private anticipationSoundInstance: SoundInstance | undefined = new SoundInstance();

  private isExpandingWild: boolean = false;

  /**
   * Constructor for initializing the reel with data and an index
   * @param {string[]} reelData - An array of symbol data for the reel
   * @param {number} index - The index of the reel (for identification)
   */
  constructor(reelData: string[], index: number) {
    super();
    this.spinSpeed = SPIN_SPEED_NORMAL;
    this.stopSpeed = STOP_SPEED_NORMAL;
    this.index = index; // Store the reel's index
    this.reelData = reelData; // Set the reel data
    this.content = new PIXI.Container();
    this.addChild(this.content); // Add the content container to the reel
    this._createSymbols(); // Create the symbols on the reel
    this._createAnticipation(); // Create the symbols on the reel
  }

  /**
   * Set new data for the reel (reel's symbols)
   * @param {string[]} reelData - An array of new symbol data for the reel
   */
  public setData(reelData: string[]): void {
    this.reelData = reelData;
  }

  /**
   * Helper function to calculate the y-position of symbols based on row index
   * @param {number} row - The row index of the symbol
   * @returns {number} - The calculated y-position for the symbol
   */
  private _getY(row: number): number {
    return Symbol.SYMBOL_HEIGHT * row + Symbol.SYMBOL_HEIGHT / 2;
  }

  public getState() {
    return this.currentState;
  }
  /**
   * Create symbols and populate the reel.
   */
  private _createSymbols(): void {
    for (let i = 0; i < this.reelData.length; i++) {
      const symbol: Symbol = Symbol.pool.acquire();
      symbol.setType(this.reelData[i].toString());
      symbol.y = this._getY(i);
      this.content.addChild(symbol);

      this.symbols.push(symbol);
      symbol.originalParent = this.content;
      symbol.originalPosition = symbol.position.clone();
    }
  }

  public hasExpandedWild(): boolean {
    return this.isExpandingWild;
  }

  public async expandWild(): Promise<void> {
    const promises: Promise<void>[] = [];
    if (this.reelData.includes("200")) {
      // Find the symbol with type "200"
      const wildIndex = this.symbols.findIndex((symbol) => symbol.getType() === "200");
      if (wildIndex !== -1) {
        const wildSymbol = this.symbols[wildIndex];
        // Move the wild symbol to the first index
        this.symbols.splice(wildIndex, 1);
        this.symbols.unshift(wildSymbol);
      }
      this.isExpandingWild = true; // Set the flag to indicate wild expansion
      this.symbols.forEach((symbol, i) => {
        if (symbol.getType() === "200") {
          promises.push(symbol.expandWild());
          gsap.to(symbol, {
            y: this._getY(0),
            duration: 0.4,
            ease: "back.out(1)",
          });
        } else {
          promises.push(symbol.hideSymbol());
        }
      });
    }

    await Promise.all(promises);
  }
  /**
   * Create symbols and populate the reel.
   */
  private _createAnticipation(): void {
    this.anticipationSkeleton = Spine.from({
      skeleton: `anticipationJson`,
      atlas: `anticipationAtlas`,
    });
    this.addChild(this.anticipationSkeleton);
    this.anticipationSkeleton.scale.set(0.2, 0.187

    );
    this.anticipationSkeleton.position.set(
      0,
      this.anticipationSkeleton.height / 2 - 15,
    );
    this.anticipationSkeleton.zIndex = 10;

    this.anticipationSkeleton.visible = false;
    this.anticipationSkeleton.state.addListener({
      complete: (entry) => {
        if (entry.animation?.name === "hide") {
          this.anticipationSkeleton.visible = false;
        }
      },
    });
  }

  /**
   * Change the state of the reel and trigger corresponding actions
   * @param {ReelState} newState - The new state to transition to
   */
  private _changeState(newState: ReelState): void {
    if (this.currentState === newState) return; // Avoid unnecessary state changes

    this.currentState = newState; // Update the current state
    switch (this.currentState) {
      case ReelState.IDLE:
        this._onEnterIdle(); // Handle entering IDLE state
        break;
      case ReelState.SPIN:
        this._onEnterSpin(); // Handle entering SPIN state
        break;
      case ReelState.STOP:
        this._onEnterStop(); // Handle entering STOP state
        break;
      case ReelState.SPIN_END:
        this.symbols.forEach((symbol) => {
          symbol.alpha = 0.8;
        });
        this._spinStep(); // Call the spin step when the animation completes
        break;
    }
    this.emit(this.currentState, this);
  }

  /**
   * Handle actions when entering IDLE state
   * Resets the stop flags.
   */
  private _onEnterIdle(): void {
    this.quickStop = false; // Reset quickStop flag
    this.isStop = false; // Reset stop flag
    this.rowsToHide.length = 0;
  }

  /**
   * Handle actions when entering SPIN state
   * Creates a new symbol offscreen and starts the spinning animation.
   */
  private _onEnterSpin(): void {
    this.quickStop = false;

    // Create a new symbol and set its position offscreen above the reel
    const symbol: Symbol = Symbol.pool.acquire();
    symbol.alpha = 1.0;
    symbol.setType(Math.ceil(Math.random() * 8).toString());
    symbol.originalParent = this.content;
    symbol.y = -Symbol.SYMBOL_HEIGHT / 2;
    this.content.addChild(symbol); // Add the symbol to the reel container

    this.symbols.unshift(symbol); // Add the symbol to the beginning of the symbol array

    // Start spinning animation with GSAP
    this.tween = gsap.to(this.content, {
      delay: this.delay, // Apply delay before starting the animation
      ease: "back.in(1)", // Animation easing function
      y: Symbol.SYMBOL_HEIGHT, // Move the reel down by one symbol height
      duration: 0.3, // Duration of the spin animation
      onComplete: () => {
        this._changeState(ReelState.SPIN_END);
      },
    });
  }

  /**
   * Step of the spinning animation, continues spinning or stops based on the stop flag.
   */
  private _spinStep(): void {
    // Only stop if the stop flag is true AND we actually have data to show
    const canStop = this.isStop && this.reelData && this.reelData.length > 0;

    if (canStop) {
      this.isStop = false;
      this._changeState(ReelState.STOP);
    } else {
      const symbol: Symbol = this.symbols.pop() as Symbol;

      // Use random symbols while waiting for real data
      symbol.setType(Math.ceil(Math.random() * 8).toString());
      this.symbols.unshift(symbol);

      const referenceSymbol = this.symbols[1];
      symbol.y = referenceSymbol.y - Symbol.SYMBOL_HEIGHT;

      this.tween = gsap.to(this.content, {
        y: this.content.y + Symbol.SYMBOL_HEIGHT,
        duration: this.spinSpeed,
        ease: "linear",
        onComplete: () => this._spinStep(),
      });
    }
  }

  /**
   * Handle actions when entering STOP state
   * Stops the spinning and repositions the symbols.
   */
  private _onEnterStop(): void {
    // 1. Move old symbols to their final landing zone (relative to content)
    this.symbols.forEach((symbol) => {
      symbol.y += this.content.y + GameEngine.getInstance().config.rows * Symbol.SYMBOL_HEIGHT;
      symbol.alpha = 1;
    });

    // 2. Snap content to its offset position
    this.content.y = -GameEngine.getInstance().config.rows * Symbol.SYMBOL_HEIGHT;

    // 3. IMPORTANT: Clear the symbols array but KEEP references to release them later
    // We want the symbols array to ONLY contain the new result symbols from this point on
    const symbolsToRelease = [...this.symbols];
    this.symbols = [];

    // 4. Create new symbols (The actual Result)
    for (let i = 0; i < GameEngine.getInstance().config.rows; i++) {
      const symbol: Symbol = Symbol.pool.acquire();
      symbol.setType(this.reelData[i].toString());
      symbol.alpha = 1.0;
      symbol.originalParent = this.content;
      symbol.y = this._getY(i);
      this.content.addChild(symbol);

      this.symbols.push(symbol); // Now index 0 is row 0, index 1 is row 1, etc.

      if (this.rowsToHide.includes(i)) {
        symbol.alpha = 0;
      }
    }

    // 5. Animate the container back to 0
    gsap.to(this.content, {
      y: 0,
      duration: this.quickStop ? 0.2 : this.stopSpeed,
      ease: "power.in(1)",
      onComplete: () => {
        // 6. Release the OLD symbols we saved in step 3
        symbolsToRelease.forEach((symbol) => {
          Symbol.pool.release(symbol);
        });

        // 7. Final alignment for new symbols
        this.symbols.forEach((symbol, i) => {
          symbol.y = this._getY(i);
          symbol.alpha = 1;
          symbol.land();
        });

        this.content.y = 0;
        this.stopAnticipation();
        this._changeState(ReelState.IDLE);
      },
    });
  }

  /**
 * Set the row indexes that should be hidden when the reel stops.
 * @param {number[]} rows - The indexes of the rows to hide.
 */
  public addRowToHide(row: number): void {
    this.rowsToHide.push(row);
  }

  public resetHiddenSymbols(): void {
    this.rowsToHide.length = 0;
    this.symbols.forEach((symbol) => {
      symbol.alpha = 1;
    });
  }

  /**
   * Move a symbol to a specific row with an animation.
   * @param {Symbol} symbol - The symbol to move
   * @param {number} [row=0] - The target row index to move the symbol to (defaults to 0)
   * @returns {Promise<void>} - A promise that resolves when the animation is complete
   */
  public async moveSymbol(symbol: Symbol, row: number = 0): Promise<void> {
    return new Promise<void>((resolve) => {
      this.symbols[row] = symbol; // Update the symbol in the specified row
      gsap.to(symbol, {
        y: this._getY(row), // Move the symbol to the target row's y-position
        duration: this.quickStop ? 0.2 : 0.4, // Quick or normal stop duration
        ease: "back.out(1)", // Easing for the animation
        onComplete: resolve, // Resolve the promise when the animation is done
      });
    });
  }

  /**
   * Get the index of the reel.
   * @returns {number} - The index of the reel
   */
  public getIndex(): number {
    return this.index;
  }

  /**
   * Get a specific symbol by index, and optionally remove it from the reel.
   * @param {number} index - The index of the symbol to retrieve
   * @param {boolean} [remove=false] - Whether to remove the symbol from the reel (defaults to false)
   * @returns {Symbol} - The symbol at the specified index
   */
  public getSymbol(index: number, remove: boolean = false): Symbol {
    const symbol = this.symbols[index];
    if (remove) {
      delete this.reelData[index]; // Optionally remove symbol data
      delete this.symbols[index]; // Optionally remove symbol from array
    }
    return symbol;
  }

  /**
   * Trigger the spin.
   */
  public triggerSpin(): void {
    this._changeState(ReelState.SPIN); // Change to SPIN state
  }

  public resetData() {
    this.reelData = [];
    this.quickStop = false;
    this.isExpandingWild = false;
  }
  /**
   * Trigger the stop action during the spin.
   * Sets the flag to stop the reel once the spin completes.
   */
  public triggerStop(quickStop: boolean = false): void {
    this.quickStop = quickStop;
    // this.isStop = true;
    if (this.currentState === ReelState.SPIN_END) {
      this.isStop = true; // Set the stop flag
    }
  }

  /**
   * Remove a symbol from a specific row after a win.
   * @param {number} row - The row index of the symbol to remove
   * @returns {Promise<void>} - A promise that resolves when the symbol has been removed
   */
  public async removeSymbol(row: number): Promise<void> {
    const symbol = this.symbols[row];
    await symbol.win(); // Perform any win-related actions
    this.removeChild(symbol); // Remove the symbol from the reel
    Symbol.pool.release(symbol); // Release the symbol back to the pool
  }


  public async startAnticipation() {
    this.spinSpeed = SPIN_SPEED_ANTICIPATION;
    this.stopSpeed = STOP_SPEED_ANTICIPATION;
    this.anticipationSkeleton.visible = true;
    this.anticipationSkeleton.state.setAnimation(0, "show", false);
    this.anticipationSkeleton.state.addAnimation(0, "idle", true);
  }

  public stopAnticipation() {
    this.spinSpeed = SPIN_SPEED_NORMAL;
    this.stopSpeed = STOP_SPEED_NORMAL;
    this.anticipationSkeleton.state.setAnimation(0, "hide", false);
  }

  public getScattersAmount() {
    return this.reelData.filter((symbol) => symbol === "11").length;
  }
}
